import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, Loader2, CheckCircle } from 'lucide-react';
import { getCallable } from '@/config/firebase';
import { toast } from 'sonner';

interface EmailResultsDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  toolName: string;
  resultSummary: string;
  resultDetails: Record<string, any>;
}

export const EmailResultsDialog: React.FC<EmailResultsDialogProps> = ({ 
    isOpen, onOpenChange, toolName, resultSummary, resultDetails 
}) => {
  const [email, setEmail] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsSending(true);
    try {
      const sendFn = await getCallable('sendToolResult');
      await sendFn({
          email,
          toolName,
          resultSummary,
          resultDetails
      });
      setIsSent(true);
      toast.success("Results emailed successfully!");
      
      // Auto-close after a moment
      setTimeout(() => {
          onOpenChange(false);
          setIsSent(false); // Reset for next time
          setEmail('');
      }, 2500);

    } catch (error: any) {
      console.error(error);
      toast.error("Failed to email results. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Email My Results</DialogTitle>
          <DialogDescription>
            Get a permanent copy of your {toolName} results sent to your inbox.
          </DialogDescription>
        </DialogHeader>

        {isSent ? (
            <div className="py-8 text-center animate-in fade-in zoom-in-95">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle size={32} />
                </div>
                <h3 className="text-lg font-semibold text-green-700 dark:text-green-400">Email Sent!</h3>
                <p className="text-sm text-muted-foreground mt-2">Check your inbox (and spam folder).</p>
            </div>
        ) : (
            <form onSubmit={handleSend} className="space-y-4 py-4">
            <div className="space-y-2">
                <Label htmlFor="email-result">Email Address</Label>
                <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input 
                        id="email-result" 
                        type="email" 
                        placeholder="you@company.com" 
                        className="pl-9" 
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={isSending}
                    />
                </div>
            </div>
            <div className="text-xs text-muted-foreground">
                We'll also send you occasional tips on SWPPP compliance. Unsubscribe anytime.
            </div>
            <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={isSending}>Cancel</Button>
                <Button type="submit" disabled={isSending || !email}>
                    {isSending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Send Report
                </Button>
            </DialogFooter>
            </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
